import {
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  VisuallyHidden,
  useMantineTheme,
} from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { Button, LoadingIndicator } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { useEffect, useState } from 'react';
import { useIsSignInMobile } from '../views/sign-in-up/useIsSignInMobile';
import { Layout } from './Layout';
import { OnboardingSidebarCard } from './OnboardingSidebarCard';
import { partnerSetupTips, vrtrustSetupTips } from '../setup';

type Props = (
  | {
      requiresPayment: true;
      phase: 'account' | 'workspace' | 'payment' | 'ready';
    }
  | {
      requiresPayment: false;
      phase: 'account' | 'workspace' | 'ready';
    }
) & {
  delayed?: { onRetry: () => void; message?: string };
  teamType?: 'partner' | 'propertyManager';
};

export const SetupLoadingScreen = ({
  phase,
  requiresPayment,
  delayed,
  teamType,
}: Props) => {
  const [tipIndex, setTipIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const reducedMotion = useReducedMotion();
  const isMobile = useIsSignInMobile();
  const { primaryColor } = useMantineTheme();
  const tips =
    teamType === 'partner'
      ? partnerSetupTips
      : teamType === 'propertyManager'
        ? vrtrustSetupTips
        : [];
  const tip = tips.length > 0 ? tips[tipIndex % tips.length] : undefined;
  const steps = [
    { id: 'email', label: 'Email verified', pending: 'Verifying your email' },
    {
      id: 'account',
      label: 'Account created',
      pending: 'Creating your account',
    },
    {
      id: 'workspace',
      label: 'Workspace ready',
      pending: 'Preparing your workspace',
    },
    ...(requiresPayment
      ? [
          {
            id: 'payment',
            label: 'Payment form ready',
            pending: 'Preparing your payment details',
          },
        ]
      : []),
  ];
  const currentStep =
    phase === 'ready'
      ? steps.length
      : steps.findIndex((step) => step.id === phase);

  useEffect(() => {
    if (isHovered || isFocused || tips.length < 2) return;

    const interval = window.setInterval(() => {
      setTipIndex((index) => (index + 1) % tips.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [isHovered, isFocused, tips.length]);

  return (
    <Layout
      title={
        phase === 'ready'
          ? 'Your workspace is ready'
          : 'We’re setting up your account'
      }
      description={
        phase === 'ready'
          ? 'Everything is ready for your next step.'
          : delayed
            ? (delayed.message ??
              'Setup is taking a little longer than expected.')
            : 'We’re getting everything ready for your first steps.'
      }
      GL={undefined}
      menu={null}
      footer={null}
      sidebar={
        tip && (
          <Stack
            component="section"
            aria-label="Setup tips"
            gap="md"
            w="100%"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocusCapture={() => setIsFocused(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setIsFocused(false);
            }}
          >
            <OnboardingSidebarCard
              title="While you wait"
              items={[
                {
                  id: tip.title,
                  title: tip.title,
                  description: tip.description,
                  icon: 'Activity2Icon',
                  link: tip.href
                    ? { href: tip.href, label: tip.linkLabel }
                    : undefined,
                },
              ]}
            />
            {tips.length > 1 && (
              <Group justify="space-between" gap="xs">
                <Text size="xs" c={isMobile ? 'neutral' : 'white'}>
                  {(tipIndex % tips.length) + 1} / {tips.length}
                </Text>
                <Button
                  variant="primary"
                  onClick={() =>
                    setTipIndex((index) => (index + 1) % tips.length)
                  }
                >
                  Next tip
                </Button>
              </Group>
            )}
          </Stack>
        )
      }
    >
      <Stack w="100%" gap="xl">
        <Box role="status" aria-live="polite" aria-atomic="true">
          <Stack
            component="ol"
            aria-label="Setup progress"
            gap="lg"
            m={0}
            p={0}
            sx={{ listStyle: 'none' }}
          >
            {steps.map((step, index) => (
              <Group
                component="li"
                key={step.id}
                aria-current={index === currentStep ? 'step' : undefined}
                gap="md"
                wrap="nowrap"
              >
                <ThemeIcon
                  aria-hidden="true"
                  size="lg"
                  radius="xl"
                  variant={index <= currentStep ? 'light' : 'outline'}
                  color={index <= currentStep ? primaryColor : 'neutral'}
                  sx={{ flexShrink: 0 }}
                >
                  {index < currentStep ? (
                    <Icon icon="CheckIcon" size={16} />
                  ) : index === currentStep && !delayed ? (
                    reducedMotion ? (
                      <Icon icon="ClockIcon" size={16} />
                    ) : (
                      <LoadingIndicator size="xs" type="oval" />
                    )
                  ) : (
                    <Icon icon="ClockIcon" size={16} />
                  )}
                </ThemeIcon>
                <Text
                  fw={index === currentStep ? 500 : 400}
                  c={index > currentStep ? 'neutral' : undefined}
                  size="sm"
                >
                  {index < currentStep
                    ? step.label
                    : `${step.pending}${index === currentStep && !delayed ? '…' : ''}`}
                  <VisuallyHidden>
                    {index < currentStep
                      ? ', complete'
                      : index === currentStep
                        ? delayed
                          ? ', delayed'
                          : ', in progress'
                        : ', waiting'}
                  </VisuallyHidden>
                </Text>
              </Group>
            ))}
          </Stack>
        </Box>
        {delayed && phase !== 'ready' && (
          <Stack gap="md">
            <Text size="sm" c="neutral">
              Your completed steps are saved. Try again to continue setting up
              your account.
            </Text>
            <Button variant="primary" onClick={delayed.onRetry}>
              Try again
            </Button>
          </Stack>
        )}
      </Stack>
    </Layout>
  );
};
